import { useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import { getSocket } from "../lib/socket";
import type { Alert } from "../lib/types";

export default function Alerts() {
  const { data, error, reload } = useFetch<Alert[]>("/api/alerts");

  useEffect(() => {
    const socket = getSocket();
    const onNew = () => reload();
    socket.on("alert:new", onNew);
    return () => {
      socket.off("alert:new", onNew);
    };
  }, [reload]);

  async function ack(a: Alert) {
    if (!a.deviceId) return;
    await api(`/api/devices/${a.deviceId}/acknowledge-alerts`, {
      method: "POST",
    });
    reload();
  }

  const grouped = (data ?? []).reduce<Record<string, Alert[]>>((acc, a) => {
    const k = a.client?.name ?? "Unknown client";
    (acc[k] ||= []).push(a);
    return acc;
  }, {});

  return (
    <>
      <div className="topbar">
        <h1>Alerts</h1>
        <div className="spacer" />
        <span className="muted">{data?.length ?? 0} unacknowledged</span>
      </div>

      {error && <div className="error">{error}</div>}

      {Object.entries(grouped).map(([client, list]) => (
        <div className="card" key={client} style={{ marginBottom: 16 }}>
          <h2>{client}</h2>
          <table>
            <thead>
              <tr>
                <th>Severity</th>
                <th>Title</th>
                <th>Device</th>
                <th>Raised</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.id}>
                  <td>
                    <span className={`pill dot ${a.severity}`}>
                      {a.severity}
                    </span>
                  </td>
                  <td>
                    {a.title}
                    {a.detail && (
                      <div className="muted" style={{ fontSize: 12 }}>
                        {a.detail}
                      </div>
                    )}
                  </td>
                  <td>
                    {a.deviceId ? (
                      <Link to={`/devices/${a.deviceId}`}>
                        {a.device
                          ? `${a.device.hostname} (${a.device.username})`
                          : "device"}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="muted">
                    {new Date(a.createdAt).toLocaleString()}
                  </td>
                  <td>
                    {a.deviceId && (
                      <button onClick={() => ack(a)}>Acknowledge</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {data?.length === 0 && <div className="muted">Nothing to acknowledge.</div>}
    </>
  );
}
